
import Vue, { PropType } from 'vue';
import { useStore as useProductStore } from '@/stores/v2/product';
import { mm2px } from '@midocean/webapps-shared-domain';
import { mapActions, mapState } from 'pinia';

export default Vue.extend({
  name: 'Ruler',
  props: {
    zoomLevel: {
      type: Number,
      required: false,
      default: 1,
    },
    containerHeight: {
      type: Number,
      required: false,
      default: 1,
    },
    containerWidth: {
      type: Number,
      required: false,
      default: 1,
    },
    canvasSelection: {
      type: Object as PropType<{
        x: number;
        y: number;
        width: number;
        height: number;
      }>,
      required: false,
      default: { x: 0, y: 0, width: 0, height: 0 },
    },
  },
  data() {
    return {
      minMillimeterValue: 1,
      minTopDistance: 75,
      minLeftDistance: 1,
      correctionFactor: 35,
    };
  },
  computed: {
    ...mapState(useProductStore, ['selectedPrintPosition']),

    numberOfSpaces(): number {
      return this.lowerTicksSpace < 10 || this.lowerTicksSpace > 25 ? 10 : 5;
    },
    lowerTicksSpace(): number {
      return this.getMinLowerTicksSpace();
    },
    higherTicksSpace(): number {
      return this.numberOfSpaces * this.lowerTicksSpace;
    },

    points(): number[] {
      let millimeters: number[] = [];
      let counter = 1;
      while (counter * this.lowerTicksSpace < this.width) {
        millimeters.push(
          counter * this.numberOfSpaces * this.minMillimeterValue
        );
        counter++;
      }
      return millimeters;
    },

    width(): number {
      const maxWidth = this.containerWidth - this.correctionFactor + 10;

      if (
        this.leftDistance === this.minLeftDistance &&
        this.canvasSelection.x < 0
      ) {
        const maxCanvas =
          this.canvasSelection.width - Math.abs(this.canvasSelection.x);

        return maxCanvas > this.containerWidth - this.correctionFactor
          ? this.containerWidth - this.correctionFactor + 10
          : maxCanvas;
      }

      return this.canvasSelection.width + this.correctionFactor > maxWidth
        ? maxWidth
        : this.canvasSelection.width + this.correctionFactor;
    },

    height(): number {
      const maxHeight = this.containerHeight - 4.4 * this.correctionFactor;
      return this.canvasSelection.height > maxHeight
        ? maxHeight
        : this.canvasSelection.height;
    },

    topDistance(): number {
      const distance = this.canvasSelection.y - this.correctionFactor + 5;
      return distance < this.minTopDistance ? this.minTopDistance : distance;
    },

    leftDistance(): number {
      const distance = this.canvasSelection.x - this.correctionFactor;
      return distance < this.minLeftDistance ? this.minLeftDistance : distance;
    },
  },
  methods: {
    ...mapActions(useProductStore, ['printPositionArea']),
    getMinLowerTicksSpace(): number {
      if (this.selectedPrintPosition.id) {
        const scaledArea = this.printPositionArea(
          this.selectedPrintPosition.id,
          true
        );

        const pixelValueOnProduct = mm2px(
          this.minMillimeterValue,
          this.selectedPrintPosition.originalImageWebDpi
        );

        const minLowerTicksSpace =
          pixelValueOnProduct * scaledArea!.scaleFactor * this.zoomLevel;

        if (minLowerTicksSpace < 5) {
          this.minMillimeterValue = 5;

          return (
            mm2px(
              this.minMillimeterValue,
              this.selectedPrintPosition.originalImageWebDpi
            ) *
            scaledArea!.scaleFactor *
            this.zoomLevel
          );
        }
        if (minLowerTicksSpace > 25) {
          this.minMillimeterValue = 1;
          return (
            mm2px(
              this.minMillimeterValue,
              this.selectedPrintPosition.originalImageWebDpi
            ) *
            scaledArea!.scaleFactor *
            this.zoomLevel
          );
        }
        return minLowerTicksSpace;
      }
      //Return something to avoid infinite loop
      return 1;
    },
  },
});
